import { AlertDialog, Button, Code, Flex, IconButton, Text } from '@radix-ui/themes'
import { ReloadIcon, TrashIcon } from '@radix-ui/react-icons'
import { Modal } from '@Components/common/modal'
import { useState } from 'react'

export const DeleteKeyAlert = ({
  description,
  onDelete,
}: {
  description: string
  onDelete: () => void
}) => {
  return (
    <AlertDialog.Root>
      <AlertDialog.Trigger>
        <IconButton variant="ghost" color="red" style={{ cursor: 'pointer' }}>
          <TrashIcon />
        </IconButton>
      </AlertDialog.Trigger>
      <AlertDialog.Content maxWidth="450px">
        <AlertDialog.Title>Delete API Key</AlertDialog.Title>
        <AlertDialog.Description size="2">
          Are you sure you want to delete the key &quot;{description}&quot;? This action cannot be
          undone. Any services using this key will immediately lose access.
        </AlertDialog.Description>

        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray" style={{ cursor: 'pointer' }}>
              Cancel
            </Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button variant="solid" color="red" style={{ cursor: 'pointer' }} onClick={onDelete}>
              Delete
            </Button>
          </AlertDialog.Action>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  )
}

export const RotateKeyAlert = ({
  description,
  onRotate,
}: {
  description: string
  onRotate: () => void
}) => {
  return (
    <AlertDialog.Root>
      <AlertDialog.Trigger>
        <IconButton variant="ghost" style={{ cursor: 'pointer' }}>
          <ReloadIcon />
        </IconButton>
      </AlertDialog.Trigger>
      <AlertDialog.Content maxWidth="450px">
        <AlertDialog.Title>Rotate API Key</AlertDialog.Title>
        <AlertDialog.Description size="2">
          Are you sure you want to rotate the key &quot;{description}&quot;? The current key value
          will be immediately invalidated and a new key will be generated.
        </AlertDialog.Description>

        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray" style={{ cursor: 'pointer' }}>
              Cancel
            </Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button variant="solid" color="orange" style={{ cursor: 'pointer' }} onClick={onRotate}>
              Rotate
            </Button>
          </AlertDialog.Action>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  )
}

export const KeyRevealModal = ({
  title,
  encodedKey,
  onDismiss,
}: {
  title: string
  encodedKey: string
  onDismiss: () => void
}) => {
  const [copied, setCopied] = useState(false)

  return (
    <Modal
      onOpenChange={(open) => {
        if (!open) {
          onDismiss()
        }
      }}
      title={title}
      description="The new API key value is shown below. Copy it now - it will not be shown again."
      hideCloseButton>
      <Flex direction="column" gap="4">
        <Text size="2" weight="bold">
          Your New API Key
        </Text>
        <Text size="2" color="red">
          This key will only be shown once. Copy it now.
        </Text>
        <Code
          size="2"
          style={{
            wordBreak: 'break-all',
            padding: '12px',
            display: 'block',
          }}>
          {encodedKey}
        </Code>
        <Flex gap="3" justify="end">
          <Button
            variant="soft"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(encodedKey)
              setCopied(true)
            }}>
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
          <Button style={{ cursor: 'pointer' }} onClick={onDismiss}>
            {"I've copied the key"}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
